import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Eye, ShieldCheck, Lock, User } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate("/", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(username.trim(), password);
    setLoading(false);
    if (res.ok) navigate("/", { replace: true });
    else setError(res.error);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#0F172A]">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between p-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: "radial-gradient(#fff 1px, transparent 1px)", backgroundSize: "22px 22px" }} />
        <div className="relative z-10 flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-[#1E40AF] flex items-center justify-center">
            <Eye className="h-6 w-6 text-white" />
          </div>
          <div>
            <div className="text-white font-head text-xl font-bold tracking-tight">Ottica CRM</div>
            <div className="text-slate-400 text-xs uppercase tracking-widest">Privacy by Design</div>
          </div>
        </div>
        <div className="relative z-10 space-y-6">
          <h1 className="text-white font-head text-4xl font-bold leading-tight">
            Gestione clienti,<br />prescrizioni e richiami<br />conforme al GDPR.
          </h1>
          <div className="space-y-3 text-slate-300 text-sm">
            <div className="flex items-center gap-3"><ShieldCheck className="h-5 w-5 text-emerald-400" /> Dati marketing separati dai dati ottici</div>
            <div className="flex items-center gap-3"><Lock className="h-5 w-5 text-sky-400" /> Accessi tracciati e ruoli dedicati</div>
          </div>
        </div>
        <div className="relative z-10 text-slate-500 text-xs">© 2026 Ottica Moderna — V1</div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 lg:p-12 bg-[#F8FAFC]">
        <form onSubmit={submit} className="w-full max-w-sm space-y-6 animate-fade-up" data-testid="login-form">
          <div className="lg:hidden flex items-center gap-3 mb-4">
            <div className="h-10 w-10 rounded-xl bg-[#1E40AF] flex items-center justify-center">
              <Eye className="h-5 w-5 text-white" />
            </div>
            <span className="font-head text-lg font-bold">Ottica CRM</span>
          </div>
          <div>
            <h2 className="font-head text-2xl font-bold text-slate-900">Accedi</h2>
            <p className="text-sm text-slate-500 mt-1">Inserisci le tue credenziali per continuare.</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="username" className="text-xs font-semibold uppercase tracking-wider text-slate-500">Utente</Label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input id="username" data-testid="login-username-input" value={username} onChange={(e) => setUsername(e.target.value)} placeholder="Nome utente" className="pl-9 h-11" autoFocus />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-wider text-slate-500">Password</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input id="password" data-testid="login-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="pl-9 h-11" />
            </div>
          </div>
          {error && <div data-testid="login-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}
          <Button type="submit" disabled={loading} data-testid="login-submit-button" className="w-full h-11 bg-[#1E40AF] hover:bg-[#1D4ED8] text-white font-semibold">
            {loading ? "Accesso…" : "Entra"}
          </Button>
          <p className="text-xs text-slate-400 text-center flex items-center justify-center gap-1">
            <Lock className="h-3 w-3" /> Connessione protetta HTTPS
          </p>
        </form>
      </div>
    </div>
  );
}
